import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import styles from './MinecraftSkinEditor.module.scss';

interface MinecraftSkinEditorProps {
  skinUrl: string;
  onSave?: (dataUrl: string) => void;
  onCancel?: () => void;
}

const PALETTE = [
  '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#00FFFF', '#FF00FF', // Basic
  '#FFFFFF', '#C0C0C0', '#808080', '#404040', '#000000',            // Grayscale
  '#F0C090', '#7F5229', '#533010',                                  // Skin tones
  '#FF9900', '#CC6600', '#663300', '#331900'                        // Browns
];

export default function MinecraftSkinEditor({ skinUrl, onSave, onCancel }: MinecraftSkinEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [brushColor, setBrushColor] = useState('#FF0000');
  const [brushSize, setBrushSize] = useState(1);
  const [isEraser, setIsEraser] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const engineRef = useRef<SkinEditorEngine | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const engine = new SkinEditorEngine(canvasRef.current);
    engineRef.current = engine;
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (engineRef.current && skinUrl) engineRef.current.loadSkinFromUrl(skinUrl);
  }, [skinUrl]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setAnimation(isAnimating);
  }, [isAnimating]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setBrushColor(brushColor);
  }, [brushColor]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setBrushSize(brushSize);
  }, [brushSize]);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setEraserMode(isEraser);
  }, [isEraser]);

  const handleSave = () => {
    if (engineRef.current && onSave) {
      onSave(engineRef.current.getSkinDataUrl());
    }
  };

  const handleReset = () => {
    if (engineRef.current) {
      if (confirm('Reset to original skin? All changes will be lost.')) {
        engineRef.current.revertToOriginal();
      }
    }
  };

  return (
    <div className={styles.container}>
      <canvas ref={canvasRef} />
      <div className={styles.controlsOverlay}>
        <div className={styles.toolsContainer}>
          {onCancel && <button className={styles.toolButton} onClick={onCancel}>Back</button>}
          <button className={`${styles.toolButton} ${isEraser ? styles.active : ''}`} onClick={() => setIsEraser(!isEraser)}>
            {isEraser ? 'Brush' : 'Eraser'}
          </button>
          <button className={`${styles.toolButton} ${isAnimating ? styles.active : ''}`} onClick={() => setIsAnimating(!isAnimating)}>
            {isAnimating ? 'Stop' : 'Walk'}
          </button>
          <button className={styles.toolButton} onClick={handleReset}>Reset</button>
          <button className={styles.toolButton} onClick={handleSave}>Save</button>
        </div>
        <div className="text-white text-xs mb-1">Select Color</div>
        <div className={styles.colorPickerContainer}>
          {PALETTE.map(color => (
            <div key={color} className={`${styles.colorSwatch} ${brushColor === color ? styles.active : ''}`} style={{ backgroundColor: color }} onClick={() => setBrushColor(color)} />
          ))}
          <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className={styles.colorSwatch} style={{ padding: 0 }} />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Skin Editor Engine - High Fidelity (Overlays + Angled Camera)
// ------------------------------------------------------------------

class SkinEditorEngine {
  public scene!: THREE.Scene;
  public camera!: THREE.PerspectiveCamera;
  public renderer!: THREE.WebGLRenderer;
  public controls!: OrbitControls;
  public character: THREE.Group = new THREE.Group();
  private skinTexture!: THREE.CanvasTexture;
  private skinCanvas!: HTMLCanvasElement;
  private skinContext!: CanvasRenderingContext2D;
  private originalSkinData: ImageData | null = null;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private isPainting = false;
  private brushColor = '#FF0000';
  private brushSize = 1;
  private isEraser = false;
  private _isAnimating = false;
  private animationTime = 0;
  private animationFrameId: number | null = null;
  private rightArm!: THREE.Mesh;
  private leftArm!: THREE.Mesh;
  private rightLeg!: THREE.Mesh;
  private leftLeg!: THREE.Mesh;

  constructor(canvas: HTMLCanvasElement) {
    this.init(canvas);
    this.createDefaultSkin();
    this.createCharacter();
    this.setupEventListeners(canvas);
    this.animate();
  }

  init(canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    const parent = canvas.parentElement;
    const width = parent?.clientWidth || 300;
    const height = parent?.clientHeight || 400;

    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.set(-15, 15, 30);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.NoToneMapping;

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.target.set(0, 10, 0);

    // Standard lighting for accurate color editing
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(10, 20, 10);
    dirLight.castShadow = true;
    this.scene.add(dirLight);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-10, 10, -10);
    this.scene.add(dirLight2);

    // Add ground plane for shadows
    const groundGeometry = new THREE.PlaneGeometry(100, 100);
    const groundMaterial = new THREE.ShadowMaterial({ opacity: 0.3 });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    ground.receiveShadow = true;
    this.scene.add(ground);

    window.addEventListener('resize', this.onResize);
    this.onResize();
  }

  createDefaultSkin() {
    this.skinCanvas = document.createElement('canvas');
    this.skinCanvas.width = 64;
    this.skinCanvas.height = 64;
    this.skinContext = this.skinCanvas.getContext('2d')!;
    this.skinContext.fillStyle = '#AAAAAA';
    this.skinContext.fillRect(0, 0, 64, 64);
    this.skinTexture = new THREE.CanvasTexture(this.skinCanvas);
    this.skinTexture.magFilter = THREE.NearestFilter;
    this.skinTexture.minFilter = THREE.NearestFilter;
  }

  createCharacter() {
    // Use MeshLambertMaterial for accurate color representation
    const mat = new THREE.MeshLambertMaterial({
      map: this.skinTexture,
      color: 0xffffff,
    });

    const transMat = new THREE.MeshLambertMaterial({
      map: this.skinTexture,
      color: 0xffffff,
      transparent: true,
      side: THREE.DoubleSide,
      alphaTest: 0.5
    });

    const createPart = (w: number, h: number, d: number, name: string, yPos: number, xPos = 0, zPos = 0) => {
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(xPos, yPos, zPos);
      mesh.name = name;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      return { mesh, geo };
    };

    const createOverlay = (parent: THREE.Mesh, w: number, h: number, d: number) => {
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, transMat);
      mesh.position.set(0, 0, 0);
      mesh.renderOrder = 1;
      mesh.name = 'overlay';
      parent.add(mesh);
      return { mesh, geo };
    };

    const head = createPart(8, 8, 8, 'head', 16);
    this.setHeadUVs(head.geo);
    this.character.add(head.mesh);
    this.addPixelGrid(head.mesh, 8, 8, 8);
    const headOv = createOverlay(head.mesh, 9, 9, 9);
    this.setHeadOverlayUVs(headOv.geo);

    const body = createPart(8, 12, 4, 'body', 6);
    this.setBodyUVs(body.geo);
    this.character.add(body.mesh);
    this.addPixelGrid(body.mesh, 8, 12, 4);
    const bodyOv = createOverlay(body.mesh, 8.5, 12.5, 4.5);
    this.setBodyOverlayUVs(bodyOv.geo);


    const rArmGeo = new THREE.BoxGeometry(4, 12, 4);
    rArmGeo.translate(0, -6, 0);
    const rArm = new THREE.Mesh(rArmGeo, mat);
    rArm.position.set(-6, 12, 0);
    rArm.name = 'rightArm';
    this.setRightArmUVs(rArmGeo);
    this.character.add(rArm);
    this.rightArm = rArm;
    this.addPixelGrid(rArm, 4, 12, 4, 6);
    const rArmOvGeo = new THREE.BoxGeometry(4.5, 12.5, 4.5);
    rArmOvGeo.translate(0, -6, 0);
    const rArmOv = new THREE.Mesh(rArmOvGeo, transMat);
    rArmOv.name = 'overlay';
    this.setRightArmOverlayUVs(rArmOvGeo);
    rArm.add(rArmOv);


    const lArmGeo = new THREE.BoxGeometry(4, 12, 4);
    lArmGeo.translate(0, -6, 0);
    const lArm = new THREE.Mesh(lArmGeo, mat);
    lArm.position.set(6, 12, 0);
    lArm.name = 'leftArm';
    this.setLeftArmUVs(lArmGeo);
    this.character.add(lArm);
    this.leftArm = lArm;
    this.addPixelGrid(lArm, 4, 12, 4, 6);
    const lArmOvGeo = new THREE.BoxGeometry(4.5, 12.5, 4.5);
    lArmOvGeo.translate(0, -6, 0);
    const lArmOv = new THREE.Mesh(lArmOvGeo, transMat);
    lArmOv.name = 'overlay';
    this.setLeftArmOverlayUVs(lArmOvGeo);
    lArm.add(lArmOv);

    const rLegGeo = new THREE.BoxGeometry(4, 12, 4);
    rLegGeo.translate(0, -6, 0);
    const rLeg = new THREE.Mesh(rLegGeo, mat);
    rLeg.position.set(-2, 0, 0);
    rLeg.name = 'rightLeg';
    this.setRightLegUVs(rLegGeo);
    this.character.add(rLeg);
    this.rightLeg = rLeg;
    this.addPixelGrid(rLeg, 4, 12, 4, 6);
    const rLegOvGeo = new THREE.BoxGeometry(4.5, 12.5, 4.5);
    rLegOvGeo.translate(0, -6, 0);
    const rLegOv = new THREE.Mesh(rLegOvGeo, transMat);
    rLegOv.name = 'overlay';
    this.setRightLegOverlayUVs(rLegOvGeo);
    rLeg.add(rLegOv);

    const lLegGeo = new THREE.BoxGeometry(4, 12, 4);
    lLegGeo.translate(0, -6, 0);
    const lLeg = new THREE.Mesh(lLegGeo, mat);
    lLeg.position.set(2, 0, 0);
    lLeg.name = 'leftLeg';
    this.setLeftLegUVs(lLegGeo);
    this.character.add(lLeg);
    this.leftLeg = lLeg;
    this.addPixelGrid(lLeg, 4, 12, 4, 6);
    const lLegOvGeo = new THREE.BoxGeometry(4.5, 12.5, 4.5);
    lLegOvGeo.translate(0, -6, 0);
    const lLegOv = new THREE.Mesh(lLegOvGeo, transMat);
    lLegOv.name = 'overlay';
    this.setLeftLegOverlayUVs(lLegOvGeo);
    lLeg.add(lLegOv);

    this.scene.add(this.character);
  }


  onResize = () => {
    if (!this.renderer) return;
    const canvas = this.renderer.domElement;
    const width = canvas.parentElement?.clientWidth || 300;
    const height = canvas.parentElement?.clientHeight || 400;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  loadSkinFromUrl(url: string) {
    const img = new Image();
    if (!url.startsWith('data:')) img.crossOrigin = 'Anonymous';
    img.onload = () => {
      this.skinContext.clearRect(0, 0, 64, 64);
      this.skinContext.drawImage(img, 0, 0, 64, 64);
      this.originalSkinData = this.skinContext.getImageData(0, 0, 64, 64);
      this.skinTexture.needsUpdate = true;
    };
    img.src = url;
  }

  getSkinDataUrl() { return this.skinCanvas.toDataURL('image/png'); }
  setBrushColor(color: string) { this.brushColor = color; }
  setBrushSize(size: number) { this.brushSize = Math.max(1, Math.min(10, size)); }
  setEraserMode(enabled: boolean) { this.isEraser = enabled; }
  revertToOriginal() {
    if (this.originalSkinData) {
      this.skinContext.putImageData(this.originalSkinData, 0, 0);
      this.skinTexture.needsUpdate = true;
    }
  }
  setAnimation(enabled: boolean) {
    this._isAnimating = enabled;
    if (!enabled) { this.rightArm.rotation.x = this.leftArm.rotation.x = this.rightLeg.rotation.x = this.leftLeg.rotation.x = 0; }
  }

  setupEventListeners(canvas: HTMLCanvasElement) {
    canvas.addEventListener('mousedown', this.onMouseDown, true);
    canvas.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
  }
  onMouseDown = (e: MouseEvent) => {
    if (e.button === 0 && this.paint(e)) {
      this.isPainting = true;
      this.controls.enabled = false;
      e.preventDefault(); e.stopPropagation();
    }
  }
  onMouseMove = (e: MouseEvent) => { if (this.isPainting) this.paint(e); }
  onMouseUp = () => { this.isPainting = false; this.controls.enabled = true; }

  paint(event: MouseEvent): boolean {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const meshes: THREE.Mesh[] = [];
    this.character.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        meshes.push(obj as THREE.Mesh);
      }
    });

    const intersects = this.raycaster.intersectObjects(meshes, false);
    if (intersects.length > 0) {
      const uv = intersects[0].uv;
      if (uv) {
        const centerX = Math.floor(uv.x * 64);
        const centerY = Math.floor((1 - uv.y) * 64);
        // Paint with brush size (square brush)
        const halfSize = Math.floor(this.brushSize / 2);
        for (let dx = -halfSize; dx <= halfSize; dx++) {
          for (let dy = -halfSize; dy <= halfSize; dy++) {
            const x = centerX + dx;
            const y = centerY + dy;
            // Ensure we stay within bounds
            if (x >= 0 && x < 64 && y >= 0 && y < 64) {
              if (this.isEraser) {
                this.skinContext.clearRect(x, y, 1, 1);
              } else {
                this.skinContext.fillStyle = this.brushColor;
                this.skinContext.fillRect(x, y, 1, 1);
              }
            }
          }
        }

        this.skinTexture.needsUpdate = true;
        return true;
      }
    }
    return false;
  }

  dispose() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.renderer.dispose();
  }

  animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    if (this._isAnimating) {
      this.animationTime += 0.0625;
      const swing = Math.sin(this.animationTime) * 0.7;
      this.rightArm.rotation.x = swing; this.leftArm.rotation.x = -swing;
      this.rightLeg.rotation.x = -swing; this.leftLeg.rotation.x = swing;
    }
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  setFaceUVs(uvs: any, faceIndex: number, x: number, y: number, width: number, height: number, flipVertical = false) {
    const index = faceIndex * 8;
    const u1 = x / 64; const v1 = 1 - (y / 64);
    const u2 = (x + width) / 64; const v2 = 1 - ((y + height) / 64);
    const vTop = flipVertical ? v2 : v1; const vBottom = flipVertical ? v1 : v2;
    uvs[index] = u1; uvs[index + 1] = vBottom; uvs[index + 2] = u2; uvs[index + 3] = vBottom;
    uvs[index + 4] = u1; uvs[index + 5] = vTop; uvs[index + 6] = u2; uvs[index + 7] = vTop;
  }

  setHeadUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 16, 8, 8, 8, true); this.setFaceUVs(uvs, 1, 0, 8, 8, 8, true);
    this.setFaceUVs(uvs, 2, 8, 0, 8, 8, false); this.setFaceUVs(uvs, 3, 16, 0, 8, 8, false);
    this.setFaceUVs(uvs, 4, 8, 8, 8, 8, true); this.setFaceUVs(uvs, 5, 24, 8, 8, 8, true);
  }
  setBodyUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 16, 20, 4, 12, true); this.setFaceUVs(uvs, 1, 28, 20, 4, 12, true);
    this.setFaceUVs(uvs, 2, 20, 16, 8, 4, false); this.setFaceUVs(uvs, 3, 28, 16, 8, 4, false);
    this.setFaceUVs(uvs, 4, 20, 20, 8, 12, true); this.setFaceUVs(uvs, 5, 32, 20, 8, 12, true);
  }
  setRightArmUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 40, 20, 4, 12, true); this.setFaceUVs(uvs, 1, 48, 20, 4, 12, true);
    this.setFaceUVs(uvs, 2, 44, 16, 4, 4, false); this.setFaceUVs(uvs, 3, 48, 16, 4, 4, false);
    this.setFaceUVs(uvs, 4, 44, 20, 4, 12, true); this.setFaceUVs(uvs, 5, 52, 20, 4, 12, true);
  }
  setLeftArmUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 32, 52, 4, 12, true); this.setFaceUVs(uvs, 1, 40, 52, 4, 12, true);
    this.setFaceUVs(uvs, 2, 36, 48, 4, 4, false); this.setFaceUVs(uvs, 3, 40, 48, 4, 4, false);
    this.setFaceUVs(uvs, 4, 36, 52, 4, 12, true); this.setFaceUVs(uvs, 5, 44, 52, 4, 12, true);
  }
  setRightLegUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 8, 20, 4, 12, true); this.setFaceUVs(uvs, 1, 0, 20, 4, 12, true);
    this.setFaceUVs(uvs, 2, 4, 16, 4, 4, false); this.setFaceUVs(uvs, 3, 8, 16, 4, 4, false);
    this.setFaceUVs(uvs, 4, 4, 20, 4, 12, true); this.setFaceUVs(uvs, 5, 12, 20, 4, 12, true);
  }
  setLeftLegUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    this.setFaceUVs(uvs, 0, 24, 52, 4, 12, true); this.setFaceUVs(uvs, 1, 16, 52, 4, 12, true);
    this.setFaceUVs(uvs, 2, 20, 48, 4, 4, false); this.setFaceUVs(uvs, 3, 24, 48, 4, 4, false);
    this.setFaceUVs(uvs, 4, 20, 52, 4, 12, true); this.setFaceUVs(uvs, 5, 28, 52, 4, 12, true);
  }


  setHeadOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;

    const oX = 32; const oY = 0;
    this.setFaceUVs(uvs, 0, oX + 16, oY + 8, 8, 8, true); this.setFaceUVs(uvs, 1, oX + 0, oY + 8, 8, 8, true);
    this.setFaceUVs(uvs, 2, oX + 8, oY + 0, 8, 8, false); this.setFaceUVs(uvs, 3, oX + 16, oY + 0, 8, 8, false);
    this.setFaceUVs(uvs, 4, oX + 8, oY + 8, 8, 8, true); this.setFaceUVs(uvs, 5, oX + 24, oY + 8, 8, 8, true);
  }
  setBodyOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    const dy = 16;
    this.setFaceUVs(uvs, 0, 16, 20 + dy, 4, 12, true); this.setFaceUVs(uvs, 1, 28, 20 + dy, 4, 12, true);
    this.setFaceUVs(uvs, 2, 20, 16 + dy, 8, 4, false); this.setFaceUVs(uvs, 3, 28, 16 + dy, 8, 4, false);
    this.setFaceUVs(uvs, 4, 20, 20 + dy, 8, 12, true); this.setFaceUVs(uvs, 5, 32, 20 + dy, 8, 12, true);
  }
  setRightArmOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;

    const dy = 16;
    this.setFaceUVs(uvs, 0, 40, 20 + dy, 4, 12, true); this.setFaceUVs(uvs, 1, 48, 20 + dy, 4, 12, true);
    this.setFaceUVs(uvs, 2, 44, 16 + dy, 4, 4, false); this.setFaceUVs(uvs, 3, 48, 16 + dy, 4, 4, false);
    this.setFaceUVs(uvs, 4, 44, 20 + dy, 4, 12, true); this.setFaceUVs(uvs, 5, 52, 20 + dy, 4, 12, true);
  }
  setLeftArmOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;

    const dx = 16;
    this.setFaceUVs(uvs, 0, 32 + dx, 52, 4, 12, true); this.setFaceUVs(uvs, 1, 40 + dx, 52, 4, 12, true);
    this.setFaceUVs(uvs, 2, 36 + dx, 48, 4, 4, false); this.setFaceUVs(uvs, 3, 40 + dx, 48, 4, 4, false);
    this.setFaceUVs(uvs, 4, 36 + dx, 52, 4, 12, true); this.setFaceUVs(uvs, 5, 44 + dx, 52, 4, 12, true);
  }
  setRightLegOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;

    const dy = 16;
    this.setFaceUVs(uvs, 0, 8, 20 + dy, 4, 12, true); this.setFaceUVs(uvs, 1, 0, 20 + dy, 4, 12, true);
    this.setFaceUVs(uvs, 2, 4, 16 + dy, 4, 4, false); this.setFaceUVs(uvs, 3, 8, 16 + dy, 4, 4, false);
    this.setFaceUVs(uvs, 4, 4, 20 + dy, 4, 12, true); this.setFaceUVs(uvs, 5, 12, 20 + dy, 4, 12, true);
  }
  setLeftLegOverlayUVs(geo: THREE.BoxGeometry) {
    const uvs = geo.attributes.uv.array;
    // Left Leg Overlay (0, 48). Base (16, 48). Delta X = -16.
    const dx = -16;
    this.setFaceUVs(uvs, 0, 24 + dx, 52, 4, 12, true); this.setFaceUVs(uvs, 1, 16 + dx, 52, 4, 12, true);
    this.setFaceUVs(uvs, 2, 20 + dx, 48, 4, 4, false); this.setFaceUVs(uvs, 3, 24 + dx, 48, 4, 4, false);
    this.setFaceUVs(uvs, 4, 20 + dx, 52, 4, 12, true); this.setFaceUVs(uvs, 5, 28 + dx, 52, 4, 12, true);
  }

  addPixelGrid(mesh: THREE.Mesh, width: number, height: number, depth: number, yOffset = 0) {

    const positions: number[] = [];
    const halfWidth = width / 2; const halfHeight = height / 2; const halfDepth = depth / 2; const offset = 0.01;
    const addLine = (x1: number, y1: number, z1: number, x2: number, y2: number, z2: number) => { positions.push(x1, y1 - yOffset, z1, x2, y2 - yOffset, z2); };
    for (let x = -halfWidth; x <= halfWidth; x += 1) addLine(x, -halfHeight, halfDepth + offset, x, halfHeight, halfDepth + offset);
    for (let y = -halfHeight; y <= halfHeight; y += 1) addLine(-halfWidth, y, halfDepth + offset, halfWidth, y, halfDepth + offset);
    for (let x = -halfWidth; x <= halfWidth; x += 1) addLine(x, -halfHeight, -halfDepth - offset, x, halfHeight, -halfDepth - offset);
    for (let y = -halfHeight; y <= halfHeight; y += 1) addLine(-halfWidth, y, -halfDepth - offset, halfWidth, y, -halfDepth - offset);
    for (let z = -halfDepth; z <= halfDepth; z += 1) addLine(halfWidth + offset, -halfHeight, z, halfWidth + offset, halfHeight, z);
    for (let y = -halfHeight; y <= halfHeight; y += 1) addLine(halfWidth + offset, y, -halfDepth, halfWidth + offset, y, halfDepth);
    for (let z = -halfDepth; z <= halfDepth; z += 1) addLine(-halfWidth - offset, -halfHeight, z, -halfWidth - offset, halfHeight, z);
    for (let y = -halfHeight; y <= halfHeight; y += 1) addLine(-halfWidth - offset, y, -halfDepth, -halfWidth - offset, y, halfDepth);
    for (let x = -halfWidth; x <= halfWidth; x += 1) addLine(x, halfHeight + offset, -halfDepth, x, halfHeight + offset, halfDepth);
    for (let z = -halfDepth; z <= halfDepth; z += 1) addLine(-halfWidth, halfHeight + offset, z, halfWidth, halfHeight + offset, z);
    for (let x = -halfWidth; x <= halfWidth; x += 1) addLine(x, -halfHeight - offset, -halfDepth, x, -halfHeight - offset, halfDepth);
    for (let z = -halfDepth; z <= halfDepth; z += 1) addLine(-halfWidth, -halfHeight - offset, z, halfWidth, -halfHeight - offset, z);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3 });
    const lines = new THREE.LineSegments(geometry, material);
    mesh.add(lines);
  }
}
